import { Button, Flex, Stack, Title } from "@mantine/core";
import { IIssueApi } from "@schemas/issueSchema";
import { IconPlus } from "@tabler/icons-react";
import { CellEditRequestEvent, ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useMemo, useRef } from "react";
import {
  useGetProjectByProjectIdConnectedTasksQuery,
  usePutProjectByProjectIdIssueConnectedTasksUpdateOrderMutation,
} from "../../../../../../openapi";
import { AG_GRID_LOCALE_NOR } from "../../../../../agGridLocale";
import { FailedAlert } from "../../../../../components";
import { setShowFailedMessageThunk } from "../../../../../global/globalSlice";
import { useAppDispatch } from "../../../../../hooks";
import { IOtherAttributeConfigApi } from "@schemas/otherAttributesSchema";
import { getIssueActionNameOption } from "../../issueUtils";
import { useCreateIssueMutation } from "../../issueRTKLegacy";
import ConnectedTasksAndIssuesMiniTable from "../Table/ConnectedTasksAndIssuesMiniTable";
import { getConnectedTaskColumns } from "./columns/connectedTaskColumns";
import { IssueTableColumnContext } from "./columns/issueTableColumnContext";

const IssueTableDetailCellRenderer: React.FC<{
  baseColumns: ColDef<IIssueApi>[];
  columnContext: IssueTableColumnContext;
  data: IIssueApi;
  defaultColDef: ColDef<IIssueApi>;
  onCellEditRequest: (event: CellEditRequestEvent<IIssueApi>) => void;
  otherAttributesTasksConfig: IOtherAttributeConfigApi;
}> = ({ baseColumns, columnContext, data, defaultColDef, onCellEditRequest, otherAttributesTasksConfig }) => {
  const dispatch = useAppDispatch();
  const [addNewIssue] = useCreateIssueMutation();
  const [reorderConnectedTasks] = usePutProjectByProjectIdIssueConnectedTasksUpdateOrderMutation();
  const { data: tasks, isError } = useGetProjectByProjectIdConnectedTasksQuery({
    issueId: data._id,
    projectId: columnContext.projectId,
  });

  const gridRef = useRef<HTMLDivElement>(null);
  const columnsTasks = useMemo(
    () =>
      getConnectedTaskColumns({
        baseColumns,
        columnContext,
        otherAttributesTasksConfig,
      }),
    [baseColumns, columnContext, otherAttributesTasksConfig],
  );

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.style.visibility = "hidden";
    }
  }, []);

  const onRowDragEnd = async (event) => {
    if (!event) return;

    const newOrder = event.api.getRenderedNodes().map((node) => node.data._id);

    try {
      await reorderConnectedTasks({
        projectId: columnContext.projectId,
        body: {
          issueId: data._id,
          newOrder,
        },
      }).unwrap();
    } catch (error) {
      console.error(error);
      dispatch(setShowFailedMessageThunk("Feil ved endring av rekkefolge pa tilkoblede saker"));
    }
  };

  return (
    <Stack gap="xs" p="xs">
      {isError === true && <FailedAlert />}
      <Flex align="center" gap="xs">
        <Title order={6} fw={400}>
          {getIssueActionNameOption(columnContext.issueBoard?.taskNameSetting, "plural")}
        </Title>
        <Button
          size="compact-xs"
          variant="outline"
          onClick={() =>
            addNewIssue({
              projectId: columnContext.projectId,
              issueBoardId: columnContext.issueBoardId,
              issueTitle: "",
              taskOrIssue: "TASK",
              issueIdIfItIsATask: data._id,
            })
          }
        >
          <IconPlus size={14} />
        </Button>
      </Flex>
      {data.connectedTasks?.length > 0 && (
        <div ref={gridRef}>
          <AgGridReact
            popupParent={document.querySelector("body") || undefined}
            rowHeight={45}
            defaultColDef={defaultColDef}
            domLayout={"autoHeight"}
            columnDefs={columnsTasks}
            getRowId={(params) => params.data._id}
            rowData={tasks}
            stopEditingWhenCellsLoseFocus={true}
            animateRows={false}
            readOnlyEdit={true}
            onCellEditRequest={onCellEditRequest}
            suppressContextMenu={true}
            suppressCutToClipboard={true}
            maintainColumnOrder={true}
            localeText={AG_GRID_LOCALE_NOR}
            onFirstDataRendered={() => {
              if (gridRef.current) {
                gridRef.current.style.visibility = "visible";
              }
            }}
            rowDragManaged={true}
            onRowDragEnd={onRowDragEnd}
          />
        </div>
      )}
      {data.connectedTasksAndIssuesFromOtherBoards?.length > 0 && (
        <ConnectedTasksAndIssuesMiniTable
          fullWidth
          issue={data}
          issueId={data._id}
          issueBoardId={columnContext.issueBoard._id}
          projectId={columnContext.projectId}
          issueBoard={columnContext.issueBoard}
        />
      )}
    </Stack>
  );
};

export default IssueTableDetailCellRenderer;
